import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, Loader2 } from 'lucide-react';
import { APPROVER_TYPE_LABELS, type ApproverType } from '@evi/shared';
import { getDisplayErrorMessage } from '@/lib/api';
import { fetchApprovers, setApprover } from '@/lib/approvals';
import styles from './SettingsPage.module.css';

/**
 * Approvers by type (Warren, 29 Oct 2026): who may approve each kind of Approval card. Only the settings owner names or
 * removes an approver (the Api checks, and says can_change); the last approver of a type stays until someone else is
 * named; every change is audited. Staff only: the Api answers 403 to anyone else and this shows nothing.
 */
const KEY = ['approvers'];

export function ApproversSection() {
  const client = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: fetchApprovers, retry: false, staleTime: 30_000 });
  const [busy, setBusy] = useState<string | null>(null), [error, setError] = useState('');
  if (q.isError) return null;
  async function toggle(userId: string, type: ApproverType, grant: boolean) {
    setBusy(`${userId}:${type}`); setError('');
    try { await setApprover(userId, type, grant); await client.invalidateQueries({ queryKey: KEY }); await client.invalidateQueries({ queryKey: ['approvals'] }); }
    catch (err) { setError(await getDisplayErrorMessage(err, 'The change was not saved.')); } finally { setBusy(null); }
  }
  return <section className={styles.platformFeeSection} aria-labelledby="approvers-title">
    <div className={styles.platformFeeHeader}>
      <div className={styles.headerIcon}><BadgeCheck size={16} /></div>
      <div>
        <h2 id="approvers-title" className={styles.platformFeeTitle}>Approvers</h2>
        <p className={styles.platformFeeHint}>Who may approve each kind of card in Today, Approvals. Only the platform owner may approve their own proposal. Nobody approves a card about their own
          account. {q.data && !q.data.can_change ? 'Only the settings owner names approvers.' : 'Every change is audited.'}</p>
      </div>
    </div>
    {q.isLoading || !q.data ? <p className={styles.platformFeeHint}><Loader2 size={13} className={styles.spin} /> Loading.</p>
      : <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr><th style={{ textAlign: 'left', padding: '4px 6px' }}>Login</th>
          {q.data.types.map((t) => <th key={t} style={{ textAlign: 'left', padding: '4px 6px' }}>{APPROVER_TYPE_LABELS[t]}</th>)}</tr></thead>
        <tbody>{q.data.staff.map((s) => <tr key={s.id} style={{ borderTop: '1px solid var(--color-border, #dce3df)' }}>
          <td style={{ padding: '4px 6px' }}>{s.name || s.id}<br /><span style={{ opacity: 0.7 }}>{s.role === 'super_admin' ? 'Super Admin' : 'Operator'}</span></td>
          {q.data!.types.map((t) => <td key={t} style={{ padding: '4px 6px' }}>
            <input type="checkbox" aria-label={`${s.name}: ${APPROVER_TYPE_LABELS[t]}`} checked={s.types.includes(t)}
              disabled={!q.data!.can_change || busy !== null} onChange={(e) => void toggle(s.id, t, e.target.checked)} />
          </td>)}
        </tr>)}</tbody>
      </table></div>}
    {error && <p className={styles.platformFeeHint} role="alert" style={{ color: 'var(--color-err)' }}>{error}</p>}
  </section>;
}
